<template>
  <Modal
    :model-value="modelValue"
    v-bind="$attrs"
    id="person-modal"
    :size="isMobile ? 'full' : 'lg'"
    :title="title"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="text-center">
        <h2 v-if="!isTypeSelected || !!person" class="title2-bold">
          {{ !!person ? `Edit ${thirdParty.type} details` : title }}
        </h2>
        <div class="flex w-full items-center justify-center" v-else>
          <WButton type="ghost" class="absolute left-6 items-center" @click="back">
            <WidgetIcon :icon="themeIcons.chevronLeft" height="32" class="widget-secondary-icon" />
          </WButton>
          <div>
            <h2 v-if="thirdParty.type === 'other'" class="title2-bold">Add other third party details</h2>
            <h2 v-else class="title2-bold">Add {{ thirdParty.type }} details</h2>
          </div>
        </div>
        <p v-if="type === 'third party' && !isTypeSelected" class="body1 text-fontSecondary-500">
          Select the type of third party to add
        </p>
      </div>
    </template>
    <div v-if="type === 'third party'" class="flex flex-col gap-y-4">
      <template v-if="!isTypeSelected">
        <WButton
          v-for="(option, index) in thirdPartyOptions.filter((item) => !item.disabled)"
          :key="index"
          type="ghost"
          class="w-full items-start justify-between gap-4 !rounded-custom border border-backgroundSoft-600 p-4 px-4 py-3"
          @click="selectType(option.type)"
        >
          <div class="flex items-center gap-4">
            <WidgetIcon v-if="option.icon" :icon="option.icon" height="3rem" class="widget-primary-icon" />
            <div v-if="checkTheme('landrover')" class="-ml-1 mr-2 flex items-center justify-center rounded-full">
              <WidgetIcon :icon="themeIcons.chevronRight" class="widget-primary-icon size-6" />
            </div>
            <div>
              <h2 class="body1-bold text-start">{{ option.title }}</h2>
              <p class="mt-1 text-start text-fontSecondary-500">{{ option.description }}</p>
            </div>
          </div>
          <div
            v-if="
              checkTheme(
                'volvo',
                'tesla',
                'aa',
                'suzuki',
                'common',
                'cinch',
                'crashdaddy',
                'jaguar',
                'jlr',
                'referral',
                'samsung'
              )
            "
            class="flex h-12 items-center justify-center rounded-full"
          >
            <WidgetIcon :icon="themeIcons.chevronRight" class="widget-primary-icon size-6" />
          </div>
        </WButton>
      </template>
      <DriverSection
        v-else-if="['driver', 'motorcyclist'].includes(thirdParty.type)"
        :driver="thirdParty as ThirdPartyDriver"
        @error="setError"
      />
      <SimpleSection v-else :contact="thirdParty" @error="setError" />
    </div>
    <SimpleSection v-else :contact="thirdParty" @error="setError" />
    <template #footer>
      <WButton
        v-if="isTypeSelected || !!person || type === 'witness'"
        type="primary"
        class="w-full"
        :disabled="isError"
        @click="submit"
      >
        Submit
      </WButton>
      <p v-else class="cursor-pointer place-self-center text-center underline" @click="selectType('other')">
        Another type of third party? Add them here
      </p>
    </template>
  </Modal>
</template>

<script setup lang="ts" generic="T extends ThirdPartyDriver | Contact">
  import { Ref, computed, ref } from 'vue';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import { ThirdPartyDriver, Contact } from 'src/types/overlays';
  import WidgetIcon from '../WidgetIcon.vue';
  import { config, themeIcons, checkTheme } from 'src/utils/config';
  import Modal from '../Modal.vue';
  import WButton from '../Button.vue';
  import DriverSection from './DriverSection.vue';
  import SimpleSection from './SimpleSection.vue';
  import { STEP_KEYS } from 'src/constants';
  import { StepKey } from 'src/types/widget';

  const props = defineProps<{
    title: string;
    type: 'third party' | 'witness';
    person?: T;
    modelValue: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'submit', thirdParty: T): void;
    (e: 'close'): void;
    (e: 'update:modelValue', value: boolean): void;
  }>();

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isMobile = breakpoints.smaller('lg');
  const defaultContact: Contact = {
    firstName: '',
    lastName: '',
    email: '',
    phone: {
      countryCallingCode: '',
      nationalNumber: '',
      formatted: '',
    },
    address: {
      latitude: 0,
      longitude: 0,
      formattedAddress: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
    },
    type: 'witness',
  };
  const defaultDriver: ThirdPartyDriver = {
    ...defaultContact,
    type: 'driver',
    vehicle: {
      licensePlate: '',
    },
    insurance: {
      provider: '',
      policy: '',
      validFrom: '',
      validTo: '',
    },
  };
  const thirdParty = ref(
    props.person !== undefined
      ? JSON.parse(JSON.stringify(props.person))
      : props.type === 'witness'
        ? ({ ...defaultContact, type: 'witness' } as T)
        : ({} as T)
  ) as Ref<T>;
  const isError = ref(false);
  const thirdPartyOptions = computed(() => [
    {
      icon: themeIcons.value.driver,
      title: 'Add a driver',
      description: 'Collect details of others drivers involved',
      type: 'driver',
      disabled: config.value.selectedClaim
        ? (
            [
              STEP_KEYS.singleVehicleForm,
              STEP_KEYS.singleVehicleLightForm,
              STEP_KEYS.patchSingleVehicleForm,
              STEP_KEYS.optionalSingleVehicleForm,
              STEP_KEYS.optionalSingleVehiclePersonalForm,
              STEP_KEYS.repairFinderForm,
              STEP_KEYS.repairFinderFormUs,
            ] as StepKey[]
          ).includes(config.value.selectedClaim)
        : false,
    },
    {
      icon: themeIcons.value.motorcyclist,
      title: 'Add a motorcyclist',
      description: 'Collect details of motorcyclists involved',
      type: 'motorcyclist',
      disabled: config.value.selectedClaim
        ? (
            [
              STEP_KEYS.singleVehicleForm,
              STEP_KEYS.patchSingleVehicleForm,
              STEP_KEYS.optionalSingleVehicleForm,
              STEP_KEYS.optionalSingleVehiclePersonalForm,
              STEP_KEYS.repairFinderForm,
              STEP_KEYS.singleVehicleLightForm,
            ] as StepKey[]
          ).includes(config.value.selectedClaim)
        : false,
    },
    {
      icon: themeIcons.value.cyclist,
      title: 'Add a cyclist',
      description: 'Collect details of cyclists involved',
      type: 'cyclist',
    },
    {
      icon: themeIcons.value.pedestrian,
      title: 'Add a pedestrian',
      description: 'Collect details of pedestrians involved',
      type: 'pedestrian',
    },
  ]);
  const isTypeSelected = ref(!!props.person);

  function setError(error: boolean) {
    isError.value = error;
  }

  function selectType(type: string) {
    isTypeSelected.value = true;
    if (props.person) {
      thirdParty.value = {
        ...props.person,
        type,
      };
      return;
    }
    if (['driver', 'motorcyclist'].includes(type)) {
      thirdParty.value = { ...defaultDriver, type } as T;
      return;
    }
    thirdParty.value = { ...defaultContact, type } as T;
  }

  function back() {
    isTypeSelected.value = false;
  }

  function submit() {
    emit('submit', thirdParty.value);
    emit('close');
  }
</script>

<style scoped></style>
